import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useAuth } from '../contexts/AuthContext';
import { calculatePtcScore } from '../services/ptcCalculator';
import './ManualOfferEntry.css';

// ── Form state ────────────────────────────────────────────────────────────────

interface FormData {
  // Property
  address: string;
  city: string;
  county: string;
  state: string;
  zip: string;
  mls_number: string;
  // Transaction
  offer_date: string;
  final_sales_price: string;
  earnest_money_deposit: string;
  closing_date: string;
  offer_expiration_date: string;
  // Financing
  payment_method: string;
  mortgage_type: string;
  mortgage_amount: string;
  down_payment_amount: string;
  seller_concession_amount: string;
  // Parties
  buyer_names: string;   // comma-separated
  seller_names: string;
  // Contingencies
  appraisal_contingency: boolean;
  mortgage_contingency: boolean;
  inspection_contingency: boolean;
  inspection_days: string;
  sale_of_buyers_property: boolean;
  // Pre-approval
  has_pre_approval: boolean;
  pre_approval_is_true_preapproval: boolean; // true = pre-approval, false = pre-qualification
  lender_name: string;
  lender_address: string;
  lender_phone: string;
  loan_officer_name: string;
  loan_officer_nmls: string;
  loan_officer_email: string;
  pre_approval_loan_type: string;
  loan_term_months: string; // e.g. "360" for 30yr
  max_purchase_price: string;
  base_loan_amount: string;
  pre_approval_letter_date: string;
  pre_approval_expiration: string;
  // PTC input
  lender_experience_years: string;
}

const EMPTY_FORM: FormData = {
  address: '', city: '', county: '', state: '', zip: '', mls_number: '',
  offer_date: '', final_sales_price: '', earnest_money_deposit: '',
  closing_date: '', offer_expiration_date: '',
  payment_method: 'cash_with_new_mortgage', mortgage_type: 'Conventional',
  mortgage_amount: '', down_payment_amount: '', seller_concession_amount: '',
  buyer_names: '', seller_names: '',
  appraisal_contingency: false, mortgage_contingency: false,
  inspection_contingency: false, inspection_days: '',
  sale_of_buyers_property: false,
  has_pre_approval: false,
  pre_approval_is_true_preapproval: true,
  lender_name: '', lender_address: '', lender_phone: '',
  loan_officer_name: '', loan_officer_nmls: '', loan_officer_email: '',
  pre_approval_loan_type: 'Conventional', loan_term_months: '',
  max_purchase_price: '', base_loan_amount: '',
  pre_approval_letter_date: '', pre_approval_expiration: '',
  lender_experience_years: '',
};

// Populate form from partial extractedData (if any was saved before failure)
function fromExtractedData(ext: any): Partial<FormData> {
  if (!ext) return {};
  return {
    address:                ext.property?.address        || '',
    city:                   ext.property?.city           || '',
    county:                 ext.property?.county         || '',
    state:                  ext.property?.state          || '',
    zip:                    ext.property?.zip            || '',
    mls_number:             ext.property?.mls_number     || '',
    offer_date:             ext.transaction?.offer_date  || '',
    final_sales_price:      ext.transaction?.final_sales_price != null
                              ? String(ext.transaction.final_sales_price) : '',
    earnest_money_deposit:  ext.transaction?.earnest_money_deposit != null
                              ? String(ext.transaction.earnest_money_deposit) : '',
    closing_date:           ext.transaction?.closing_date           || '',
    offer_expiration_date:  ext.transaction?.offer_expiration_date  || '',
    payment_method:         ext.financing?.payment_method           || 'cash_with_new_mortgage',
    mortgage_type:          ext.financing?.mortgage_type            || 'Conventional',
    mortgage_amount:        ext.financing?.mortgage_amount != null
                              ? String(ext.financing.mortgage_amount) : '',
    down_payment_amount:    ext.financing?.down_payment_amount != null
                              ? String(ext.financing.down_payment_amount) : '',
    seller_concession_amount: ext.financing?.seller_concession_amount != null
                              ? String(ext.financing.seller_concession_amount) : '',
    buyer_names:  (ext.parties?.buyers  || []).map((b: any) => b.name).filter(Boolean).join(', '),
    seller_names: (ext.parties?.sellers || []).map((s: any) => s.name).filter(Boolean).join(', '),
    appraisal_contingency:      ext.contingencies?.appraisal === true,
    mortgage_contingency:       ext.contingencies?.mortgage  === true,
    inspection_contingency:     ext.contingencies?.inspection?.waived === false,
    inspection_days:            ext.contingencies?.inspection?.inspection_days != null
                                  ? String(ext.contingencies.inspection.inspection_days) : '',
    sale_of_buyers_property:    ext.contingencies?.sale_of_buyers_property === true,
    has_pre_approval:                   ext._meta?.has_pre_approval === true,
    pre_approval_is_true_preapproval:   ext.pre_approval?.is_pre_approval !== false,
    lender_name:                        ext.pre_approval?.lender_name          || '',
    lender_address:                     ext.pre_approval?.lender_address        || '',
    lender_phone:                       ext.pre_approval?.lender_phone          || '',
    loan_officer_name:                  ext.pre_approval?.loan_officer_name     || '',
    loan_officer_nmls:                  ext.pre_approval?.loan_officer_nmls     || '',
    loan_officer_email:                 ext.pre_approval?.loan_officer_email    || '',
    pre_approval_loan_type:             ext.pre_approval?.loan_type             || 'Conventional',
    loan_term_months:                   ext.pre_approval?.loan_term_months != null
                                          ? String(ext.pre_approval.loan_term_months) : '',
    max_purchase_price:                 ext.pre_approval?.max_purchase_price != null
                                          ? String(ext.pre_approval.max_purchase_price) : '',
    base_loan_amount:                   ext.pre_approval?.base_loan_amount != null
                                          ? String(ext.pre_approval.base_loan_amount) : '',
    pre_approval_letter_date:           ext.pre_approval?.letter_date           || '',
    pre_approval_expiration:            ext.pre_approval?.expiration_date       || '',
  };
}

// Build ExtractedData shape for Firestore from form values
function buildExtractedData(f: FormData, offerId: string): any {
  const parseNum = (v: string) => v.trim() !== '' ? parseFloat(v) : null;
  const buyers  = f.buyer_names.split(',').map(n => n.trim()).filter(Boolean).map(name => ({ name, signature_date: '' }));
  const sellers = f.seller_names.split(',').map(n => n.trim()).filter(Boolean).map(name => ({ name, signature_date: '' }));

  return {
    _meta: {
      extracted_at: new Date().toISOString(),
      source_filename: offerId,
      form_type: 'manual_entry',
      form_version: '1.0',
      document_state: '',
      has_pre_approval: f.has_pre_approval,
      addenda_present: ['purchase_agreement'],
      extraction_confidence: 'high',
      missing_fields: [],
    },
    property: {
      address: f.address, city: f.city, township: '', county: f.county,
      state: f.state, zip: f.zip, tax_id: '', legal_description: '',
      mls_number: f.mls_number, is_private_road: null, homestead_rate: null,
    },
    transaction: {
      original_sales_price: null,
      final_sales_price: parseNum(f.final_sales_price),
      sales_price_written: '',
      price_was_amended: null,
      earnest_money_deposit: parseNum(f.earnest_money_deposit),
      earnest_money_holder: '',
      closing_date: f.closing_date,
      offer_expiration_date: f.offer_expiration_date,
      offer_expiration_time: '',
      offer_date: f.offer_date,
    },
    financing: {
      payment_method: f.payment_method,
      mortgage_type: f.mortgage_type,
      mortgage_amount: parseNum(f.mortgage_amount),
      mortgage_amount_percent: null,
      down_payment_amount: parseNum(f.down_payment_amount),
      down_payment_percent: null,
      mortgage_application_days: null,
      mortgage_commitment_days: null,
      seller_concession_amount: parseNum(f.seller_concession_amount),
      seller_concession_percent: null,
    },
    parties: { buyers, sellers },
    agents: {
      buyer_broker: { company: '', agent_name: '', agent_id: '', office_id: '', email: '', phone: '', license_number: '', agency_type: '', commission_amount: null, commission_percent: null },
      seller_broker: { company: '', agent_name: '', agent_id: '', office_id: '', email: '', phone: '', license_number: '', agency_type: '' },
    },
    title_and_closing: { title_company: '', lender_title_company: '', buyer_pays_survey: null, compliance_fee: null },
    occupancy: { possession_timing: '', days_after_closing: null, per_diem_rent: null, escrow_holdback: null },
    contingencies: {
      inspection: { waived: !f.inspection_contingency, inspection_days: parseNum(f.inspection_days) as number | null, notification_days: null, seller_remedy_days: null },
      appraisal: f.appraisal_contingency,
      mortgage: f.mortgage_contingency,
      radon:       { waived: null, tested: null },
      well_septic: { waived: null, tested: null },
      survey:      { waived: null, required: null },
      flood_insurance: null, flood_certification_days: null, flood_notification_days: null,
      lead_based_paint_received: null, lead_based_paint_waived: null,
      sellers_disclosure_received: null,
      sale_of_buyers_property: f.sale_of_buyers_property,
      hoa_document_review: null,
      additional_conditions: [],
    },
    escalation_clause: { present: false, increment_amount: null, max_cap: null, verification_method: '', expires_at: '' },
    inclusions_exclusions: { additional_inclusions: '', exclusions: '' },
    home_warranty: { seller_pays: null, buyer_pays: null, waived: null },
    arbitration: { buyer_agrees: null, seller_agrees: null },
    city_certification: { seller_orders_inspection: null, buyer_orders_inspection: null, seller_makes_repairs: null, buyer_takes_as_is: null },
    hoa: { exists: null, document_delivery_days: null, review_period_days: null, transfer_fee_paid_by: '' },
    fha_va_addendum: { present: null, amendatory_clause_amount: null, seller_contribution_type: '', seller_contribution_amount: null, seller_contribution_percent: null, seller_contribution_toward: '', pest_inspection_paid_by: '', reinspection_fees_paid_by: '', seller_repair_limit: null, buyer_repair_limit: null, first_responsible_party: '' },
    buyer_broker_compensation_addendum: { present: null, compensation_amount: null, compensation_percent: null, payable_to: '' },
    pre_approval: {
      lender_name: f.lender_name,
      lender_address: f.lender_address,
      lender_phone: f.lender_phone,
      loan_officer_name: f.loan_officer_name,
      loan_officer_nmls: f.loan_officer_nmls,
      loan_officer_email: f.loan_officer_email,
      borrower_names: buyers.map(b => b.name),
      loan_type: f.pre_approval_loan_type,
      max_purchase_price: parseNum(f.max_purchase_price),
      base_loan_amount: parseNum(f.base_loan_amount),
      loan_term_months: parseNum(f.loan_term_months),
      expiration_date: f.pre_approval_expiration,
      letter_date: f.pre_approval_letter_date,
      is_pre_approval: f.has_pre_approval ? f.pre_approval_is_true_preapproval : null,
      conditions: [],
    },
  };
}

// ── Component ─────────────────────────────────────────────────────────────────

const ManualOfferEntry: React.FC = () => {
  const { offerId } = useParams<{ offerId: string }>();
  const navigate = useNavigate();
  const { userProfile } = useAuth();

  const [form, setForm] = useState<FormData>(EMPTY_FORM);
  const [offer, setOffer] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [showConfirm, setShowConfirm] = useState(false);

  // Guard: admin only
  useEffect(() => {
    if (userProfile && userProfile.role !== 'admin') navigate('/');
  }, [userProfile, navigate]);

  // Load offer
  useEffect(() => {
    if (!offerId) return;
    getDoc(doc(db, 'offers', offerId)).then((snap) => {
      if (!snap.exists()) { navigate('/review-queue'); return; }
      const data = snap.data();
      setOffer({ id: snap.id, ...data });
      setForm({ ...EMPTY_FORM, ...fromExtractedData(data.extractedData) });
      setLoading(false);
    });
  }, [offerId, navigate]);

  // ── Live PTC score ──────────────────────────────────────────────────────────

  const ptcResult = useMemo(() => {
    const loanAmount   = parseFloat(form.mortgage_amount)      || 0;
    const purchasePrice = parseFloat(form.final_sales_price)   || 0;
    const downPayment  = parseFloat(form.down_payment_amount)  || undefined;
    const loanType     = form.has_pre_approval ? form.pre_approval_loan_type : form.mortgage_type;
    const expYears     = form.lender_experience_years !== '' ? parseFloat(form.lender_experience_years) : undefined;

    const isCashOffer = form.payment_method === 'cash';

    if (!isCashOffer && loanAmount <= 0 && purchasePrice <= 0) return null;

    // If no loan amount but we have price + down payment, derive loan amount
    const effectiveLoan = loanAmount > 0 ? loanAmount
      : (purchasePrice > 0 && downPayment !== undefined ? purchasePrice - downPayment : 0);

    try {
      return calculatePtcScore({
        isCashOffer,
        inspectionWaived: form.inspection_contingency === false,
        loanAmount: effectiveLoan || undefined,
        purchasePrice: purchasePrice || undefined,
        downPayment,
        loanType,
        hasPreApproval: form.has_pre_approval,
        lenderExperienceYears: expYears,
        contingencies: {
          appraisalContingency: form.appraisal_contingency,
          homeInspectionContingency: form.inspection_contingency,
          saleOfBuyerPropertyContingency: form.sale_of_buyers_property,
        },
      });
    } catch {
      return null;
    }
  }, [
    form.mortgage_amount, form.final_sales_price, form.down_payment_amount,
    form.mortgage_type, form.pre_approval_loan_type, form.has_pre_approval,
    form.lender_experience_years,
    form.appraisal_contingency, form.inspection_contingency, form.sale_of_buyers_property,
  ]);

  const scoreColor = ptcResult
    ? ptcResult.ptcScore >= 70 ? '#16a34a'
      : ptcResult.ptcScore >= 50 ? '#d97706'
      : '#dc2626'
    : '#9ca3af';

  // ── Field helpers ───────────────────────────────────────────────────────────

  const set = (key: keyof FormData, value: any) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const textField = (
    label: string, key: keyof FormData,
    opts?: { type?: string; placeholder?: string; hint?: string }
  ) => (
    <div className="moe-field">
      <label className="moe-label">{label}</label>
      <input
        className="moe-input"
        type={opts?.type || 'text'}
        placeholder={opts?.placeholder || ''}
        value={form[key] as string}
        onChange={(e) => set(key, e.target.value)}
      />
      {opts?.hint && <span className="moe-hint">{opts.hint}</span>}
    </div>
  );

  const boolField = (label: string, key: keyof FormData, hint?: string) => (
    <div className="moe-field moe-field-bool">
      <label className="moe-label">{label}</label>
      <div className="moe-toggle-row">
        <button
          type="button"
          className={`moe-toggle ${form[key] ? 'active' : ''}`}
          onClick={() => set(key, true)}
        >Yes</button>
        <button
          type="button"
          className={`moe-toggle ${!form[key] ? 'active' : ''}`}
          onClick={() => set(key, false)}
        >No</button>
      </div>
      {hint && <span className="moe-hint">{hint}</span>}
    </div>
  );

  const selectField = (
    label: string, key: keyof FormData, options: { value: string; label: string }[]
  ) => (
    <div className="moe-field">
      <label className="moe-label">{label}</label>
      <select
        className="moe-select"
        value={form[key] as string}
        onChange={(e) => set(key, e.target.value)}
      >
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );

  // ── Save ────────────────────────────────────────────────────────────────────

  const handleComplete = async () => {
    if (!offerId) return;
    setSaving(true);
    setSaveError('');
    try {
      const extractedData = buildExtractedData(form, offerId);
      const ptc = ptcResult;

      await updateDoc(doc(db, 'offers', offerId), {
        parsingStatus: 'completed',
        processingCompleted: true,
        extractedData,
        ptcScore: ptc?.ptcScore ?? null,
        ptcBreakdown: ptc?.ptcBreakdown ?? null,
        ptcCalculatedAt: serverTimestamp(),
        lastProcessedDate: serverTimestamp(),
        manuallyReviewedBy: userProfile?.email || 'admin',
        manuallyReviewedAt: serverTimestamp(),
        parsingError: null,
      });

      navigate('/review-queue');
    } catch (err) {
      setSaveError('Failed to save. Please try again.');
      console.error(err);
    } finally {
      setSaving(false);
      setShowConfirm(false);
    }
  };

  const formatCurrency = (v: number) =>
    v.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

  // ── Render ──────────────────────────────────────────────────────────────────

  if (!userProfile || userProfile.role !== 'admin') return null;
  if (loading) return <div className="moe-loading">Loading offer...</div>;

  const files: any[] = offer?.files || [];

  return (
    <div className="moe-layout">
      {/* ── Header ── */}
      <div className="moe-header">
        <button className="back-button" onClick={() => navigate('/review-queue')}>
          ← Manual Review Queue
        </button>
        <div className="moe-header-info">
          <h1 className="moe-title">Manual Offer Entry</h1>
          <p className="moe-subtitle">
            {offer?.buyerAgentName && <span>{offer.buyerAgentName} · </span>}
            {offer?.buyerAgentEmail && <span>{offer.buyerAgentEmail} · </span>}
            Submitted {offer?.submissionDate?.toDate
              ? offer.submissionDate.toDate().toLocaleDateString()
              : '—'}
          </p>
        </div>
      </div>

      <div className="moe-body">
        {/* ── Left: Form ── */}
        <div className="moe-form-col">

          {/* PDF Files */}
          {files.length > 0 && (
            <div className="moe-section">
              <h2 className="moe-section-title">Uploaded Documents</h2>
              <div className="moe-files">
                {files.map((f, i) => (
                  f.downloadURL ? (
                    <a key={i} href={f.downloadURL} target="_blank" rel="noopener noreferrer" className="moe-file-btn">
                      📄 {f.fileName || `PDF ${i + 1}`}
                    </a>
                  ) : (
                    <span key={i} className="moe-file-nourl">📄 {f.fileName}</span>
                  )
                ))}
              </div>
            </div>
          )}

          {/* Property */}
          <div className="moe-section">
            <h2 className="moe-section-title">Property Information</h2>
            <div className="moe-grid-2">{textField('Street Address', 'address', { placeholder: '123 Main St' })}</div>
            <div className="moe-grid-3">
              {textField('City', 'city')}
              {textField('County', 'county')}
              {textField('State', 'state', { placeholder: 'MI' })}
            </div>
            <div className="moe-grid-3">
              {textField('ZIP Code', 'zip')}
              {textField('MLS #', 'mls_number')}
            </div>
          </div>

          {/* Transaction */}
          <div className="moe-section">
            <h2 className="moe-section-title">Transaction Terms</h2>
            <div className="moe-grid-2">
              {textField('Purchase Price ($)', 'final_sales_price', { type: 'number', placeholder: '350000' })}
              {textField('Earnest Money Deposit ($)', 'earnest_money_deposit', { type: 'number', placeholder: '5000' })}
            </div>
            <div className="moe-grid-3">
              {textField('Offer Date', 'offer_date', { type: 'date' })}
              {textField('Closing Date', 'closing_date', { type: 'date' })}
              {textField('Offer Expiration', 'offer_expiration_date', { type: 'date' })}
            </div>
          </div>

          {/* Financing */}
          <div className="moe-section">
            <h2 className="moe-section-title">Financing</h2>
            <div className="moe-grid-2">
              {selectField('Payment Method', 'payment_method', [
                { value: 'cash_with_new_mortgage', label: 'Cash with New Mortgage' },
                { value: 'cash', label: 'Cash' },
                { value: 'cash_with_land_contract', label: 'Cash with Land Contract' },
                { value: 'assumption', label: 'Assumption' },
                { value: 'seller_financing', label: 'Seller Financing' },
                { value: 'other', label: 'Other' },
              ])}
              {selectField('Mortgage Type', 'mortgage_type', [
                { value: 'Conventional', label: 'Conventional' },
                { value: 'FHA', label: 'FHA' },
                { value: 'VA', label: 'VA' },
                { value: 'USDA', label: 'USDA' },
                { value: 'Jumbo', label: 'Jumbo' },
                { value: 'Other', label: 'Other' },
                { value: '', label: 'Unknown' },
              ])}
            </div>
            <div className="moe-grid-3">
              {textField('Loan Amount ($)', 'mortgage_amount', { type: 'number', placeholder: '280000' })}
              {textField('Down Payment ($)', 'down_payment_amount', { type: 'number', placeholder: '70000' })}
              {textField('Seller Concessions ($)', 'seller_concession_amount', { type: 'number', placeholder: '0' })}
            </div>
          </div>

          {/* Parties */}
          <div className="moe-section">
            <h2 className="moe-section-title">Parties</h2>
            <div className="moe-grid-2">
              {textField('Buyer Name(s)', 'buyer_names', { placeholder: 'John Doe, Jane Doe' })}
              {textField('Seller Name(s)', 'seller_names', { placeholder: 'Bob Smith' })}
            </div>
          </div>

          {/* Contingencies */}
          <div className="moe-section">
            <h2 className="moe-section-title">Contingencies</h2>
            <div className="moe-grid-3">
              {boolField('Appraisal Contingency', 'appraisal_contingency')}
              {boolField('Financing / Mortgage Contingency', 'mortgage_contingency')}
              {boolField('Sale of Buyer\'s Property', 'sale_of_buyers_property')}
            </div>
            <div className="moe-grid-3">
              {boolField('Home Inspection Contingency', 'inspection_contingency')}
              {form.inspection_contingency && textField('Inspection Days', 'inspection_days', { type: 'number', placeholder: '10' })}
            </div>
          </div>

          {/* Pre-Approval */}
          <div className="moe-section">
            <h2 className="moe-section-title">Pre-Approval</h2>
            <div className="moe-grid-2">
              {boolField('Pre-Approval Letter Included?', 'has_pre_approval')}
            </div>
            {form.has_pre_approval && (
              <>
                <div className="moe-grid-2">
                  {boolField('Pre-Approval (vs Pre-Qualification)', 'pre_approval_is_true_preapproval',
                    'Pre-Approval = fully reviewed credit. Pre-Qualification = self-reported only.')}
                </div>

                <h3 className="moe-subsection-title">Lender Information</h3>
                <div className="moe-grid-2">
                  {textField('Lender / Mortgage Company', 'lender_name', { placeholder: 'Security Mortgage Corporation' })}
                  {textField('Lender Phone', 'lender_phone', { placeholder: '734-368-0819' })}
                </div>
                <div className="moe-grid-2">
                  {textField('Lender Address', 'lender_address', { placeholder: '48440 Denton #106 Belleville MI 48111' })}
                </div>

                <h3 className="moe-subsection-title">Loan Officer</h3>
                <div className="moe-grid-3">
                  {textField('Loan Officer Name', 'loan_officer_name', { placeholder: 'Christopher Pope' })}
                  {textField('Loan Officer NMLS #', 'loan_officer_nmls', { placeholder: '135633' })}
                  {textField('Loan Officer Email', 'loan_officer_email', { placeholder: 'chris@lender.com' })}
                </div>

                <h3 className="moe-subsection-title">Loan Details</h3>
                <div className="moe-grid-3">
                  {selectField('Loan Type', 'pre_approval_loan_type', [
                    { value: 'Conventional', label: 'Conventional' },
                    { value: 'FHA', label: 'FHA' },
                    { value: 'VA', label: 'VA' },
                    { value: 'USDA', label: 'USDA' },
                    { value: 'Jumbo', label: 'Jumbo' },
                    { value: 'Other', label: 'Other' },
                  ])}
                  {selectField('Loan Term', 'loan_term_months', [
                    { value: '', label: 'Unknown' },
                    { value: '360', label: '30-Year Fixed' },
                    { value: '240', label: '20-Year Fixed' },
                    { value: '180', label: '15-Year Fixed' },
                    { value: '120', label: '10-Year Fixed' },
                    { value: '84', label: '7-Year ARM' },
                    { value: '60', label: '5-Year ARM' },
                  ])}
                </div>
                <div className="moe-grid-2">
                  {textField('Max Purchase Price ($)', 'max_purchase_price', { type: 'number', placeholder: '190000' })}
                  {textField('Base Loan Amount ($)', 'base_loan_amount', { type: 'number', placeholder: '180500' })}
                </div>

                <h3 className="moe-subsection-title">Letter Dates</h3>
                <div className="moe-grid-2">
                  {textField('Letter Date', 'pre_approval_letter_date', { type: 'date' })}
                  {textField('Expiration Date', 'pre_approval_expiration', { type: 'date' })}
                </div>
              </>
            )}
          </div>

        </div>

        {/* ── Right: PTC Score + Actions ── */}
        <div className="moe-score-col">
          <div className="moe-score-card">
            <h3 className="moe-score-card-title">PTC Score Preview</h3>

            {/* Lender experience input lives here as it affects the score */}
            <div className="moe-score-exp-row">
              <label className="moe-label">Lender Experience (years)</label>
              <input
                className="moe-input"
                type="number"
                placeholder="e.g. 5 (leave blank = unknown)"
                value={form.lender_experience_years}
                onChange={(e) => set('lender_experience_years', e.target.value)}
              />
              <span className="moe-hint">Leave blank if not known (assumes &lt;1 year)</span>
            </div>

            {ptcResult ? (
              <>
                <div className="moe-score-circle" style={{ borderColor: scoreColor, color: scoreColor }}>
                  <span className="moe-score-value">{Math.round(ptcResult.ptcScore)}</span>
                  <span className="moe-score-label">/ 100</span>
                </div>

                <div className="moe-breakdown">
                  {!ptcResult.ptcBreakdown.isCashOffer && ptcResult.ptcBreakdown.hasPreApproval && (
                    <>
                      <div className="moe-breakdown-row">
                        <span>Lender Experience Base</span>
                        <span className="moe-bd-pos">+{ptcResult.ptcBreakdown.lenderExperiencePoints}</span>
                      </div>
                      {ptcResult.ptcBreakdown.ltvPoints > 0 && (
                        <div className="moe-breakdown-row">
                          <span>LTV + Loan Type</span>
                          <span className="moe-bd-pos">+{ptcResult.ptcBreakdown.ltvPoints.toFixed(1)}</span>
                        </div>
                      )}
                    </>
                  )}
                  {!ptcResult.ptcBreakdown.hasPreApproval && (
                    <div className="moe-breakdown-row">
                      <span>No Pre-Approval</span>
                      <span className="moe-bd-neg">Score = 40</span>
                    </div>
                  )}
                  {ptcResult.ptcBreakdown.isCashOffer && (
                    <div className="moe-breakdown-row">
                      <span>Cash Offer</span>
                      <span className="moe-bd-pos">Score = {ptcResult.ptcScore}</span>
                    </div>
                  )}
                  {ptcResult.ptcBreakdown.appraisalContingencyDeduction > 0 && (
                    <div className="moe-breakdown-row">
                      <span>Appraisal Contingency</span>
                      <span className="moe-bd-neg">−{ptcResult.ptcBreakdown.appraisalContingencyDeduction}</span>
                    </div>
                  )}
                  {ptcResult.ptcBreakdown.homeInspectionContingencyDeduction > 0 && (
                    <div className="moe-breakdown-row">
                      <span>Inspection Contingency</span>
                      <span className="moe-bd-neg">−{ptcResult.ptcBreakdown.homeInspectionContingencyDeduction}</span>
                    </div>
                  )}
                  {ptcResult.ptcBreakdown.saleOfBuyerPropertyContingencyDeduction > 0 && (
                    <div className="moe-breakdown-row">
                      <span>Sale of Buyer Property</span>
                      <span className="moe-bd-neg">−{ptcResult.ptcBreakdown.saleOfBuyerPropertyContingencyDeduction}</span>
                    </div>
                  )}
                  {ptcResult.ptcBreakdown.ltvPercent !== null && (
                    <div className="moe-breakdown-row moe-bd-detail">
                      <span>Calculated LTV</span>
                      <span>{ptcResult.ptcBreakdown.ltvPercent.toFixed(1)}%</span>
                    </div>
                  )}
                </div>

                <div className="moe-score-summary">
                  Purchase: ${formatCurrency(parseFloat(form.final_sales_price) || 0)} ·{' '}
                  Loan: ${formatCurrency(parseFloat(form.mortgage_amount) || 0)} ·{' '}
                  {form.has_pre_approval ? '✓ Pre-approval' : '✗ No pre-approval'}
                </div>
              </>
            ) : (
              <div className="moe-score-empty">
                Enter purchase price and loan amount to see the PTC score
              </div>
            )}

            {saveError && <p className="moe-save-error">{saveError}</p>}

            <button
              className="moe-complete-btn"
              onClick={() => setShowConfirm(true)}
              disabled={saving}
            >
              Complete Processing
            </button>
            <p className="moe-complete-hint">
              This will mark the offer as processed and make it visible to the listing agent.
            </p>
          </div>
        </div>
      </div>

      {/* ── Confirm Modal ── */}
      {showConfirm && (
        <div className="moe-overlay" onClick={() => setShowConfirm(false)}>
          <div className="moe-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="moe-modal-title">Complete Processing?</h3>
            {ptcResult && (
              <p className="moe-modal-score">
                PTC Score: <strong style={{ color: scoreColor }}>{Math.round(ptcResult.ptcScore)}</strong>
              </p>
            )}
            <p className="moe-modal-body">
              This will mark the offer as <strong>completed</strong> and make it visible to the listing agent.
              The data you entered will be saved permanently.
            </p>
            <div className="moe-modal-actions">
              <button className="moe-modal-cancel" onClick={() => setShowConfirm(false)}>
                Cancel
              </button>
              <button className="moe-modal-confirm" onClick={handleComplete} disabled={saving}>
                {saving ? 'Saving…' : 'Yes, Complete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManualOfferEntry;
